import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TrendingUp, BookOpen, Users, Award, ArrowRight, Play, CheckCircle, ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react';
import { communityApi, coursesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [memberships, setMemberships] = useState([]);
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [loadingMemberships, setLoadingMemberships] = useState(true);
  const [systemVideoPlaying, setSystemVideoPlaying] = useState(false);

  // Fetch memberships and featured courses from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [membershipsData, coursesData] = await Promise.all([
          communityApi.getMemberships(),
          coursesApi.getFeatured()
        ]);
        // Filter out memberships that are marked as hidden (visible === false)
        const visibleMemberships = membershipsData.filter(m => m.visible !== false);
        setMemberships(visibleMemberships);
        setFeaturedCourses(coursesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadingMemberships(false);
      }
    };
    fetchData();
  }, []);

  // Handle hash navigation (e.g., /#pricing)
  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.hash]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Helper to format period for display
  const formatPeriod = (period) => {
    switch (period) {
      case 'month': return '/mo';
      case '3months': return '/3mo';
      case '6months': return '/6mo';
      case 'year': return '/yr';
      case 'one-time': return '';
      default: return '/mo';
    }
  };

  const testimonials = [
    {
      quote: "Coach CJ makes trading simple and easy for a beginner like me to learn and understand. He really cares about helping his students become winning traders.",
      author: "Jim B",
      role: "HyperDrive Student"
    },
    {
      quote: "HyperDrive is Smart, Simple and Incredibly effective for mastering the art of trading",
      author: "Juliet",
      role: "HyperDrive Trader"
    },
    {
      quote: "The HyperDrive System takes the emotion out of trading. Every part of the trade has easy to follow steps to success!",
      author: "Steph",
      role: "HyperDrive Member"
    },
    {
      quote: "HyperDrive is an easy to use backtest proven strategy that flat out works!!!",
      author: "Jay Silver",
      role: "Professional Trader"
    },
    {
      quote: "Coach CJ's HyperDrive strategy is easy to learn and execute, taking the stress out of trading and helping you become a profitable trader.",
      author: "Ken",
      role: "HyperDrive Graduate"
    },
    {
      quote: "The best system to stay in the 10% in a market where 90% exit!",
      author: "Harumin",
      role: "International Trader"
    },
    {
      quote: "With his heart for teaching, and his simplistic approach, Coach CJ will have you excelling at trading.",
      author: "Christine",
      role: "HyperDrive Student"
    },
    {
      quote: "Coach CJ's amazingly simple trading system is easy to learn and if consistently applied you will trade successfully.",
      author: "Karl Hughes",
      role: "Happy Trader"
    },
    {
      quote: "The HyperDrive Way is the only WAY I trade Nas and US30",
      author: "Torey",
      role: "Futures Trader"
    }
  ];

  const features = [
    {
      icon: TrendingUp,
      title: 'Live Trading Sessions',
      description: 'Trade live with Coach CJ. Watch real-time executions on NQ/MNQ Futures and Nasdaq.',
      color: '#ef4444'
    },
    {
      icon: BookOpen,
      title: 'HyperDrive Manual',
      description: 'In-depth instructions covering alignment criteria, entry criteria, and management explanations.',
      color: '#8b5cf6'
    },
    {
      icon: Users,
      title: 'Community & Support',
      description: 'Connect with fellow traders, share insights, and grow together in our vibrant community.',
      color: '#10b981'
    },
    {
      icon: Award,
      title: 'Prop Firm Guidance',
      description: 'Learn strategies specifically designed to help you pass and manage prop firm accounts.',
      color: '#f59e0b'
    }
  ];

  const systemBenefits = [
    'Easy to understand trading system',
    'Simple to follow, duplicatable process',
    'Works for beginners and seasoned traders',
    'Recognize, Execute and Manage trades',
    'Backtest proven strategy',
    'Takes emotion out of trading'
  ];

  const pricingPlans = [
    {
      name: 'Live Trading',
      price: 99,
      period: 'month',
      description: 'Monthly Subscription',
      features: [
        'Live Execution Trading',
        'NQ/MNQ Futures Trading',
        'Nasdaq MT4/MT5/Tradelocker',
        'Real-time trade alerts',
        'Community access'
      ],
      popular: false,
      cta: 'Subscribe Now'
    },
    {
      name: 'HDOne Manual + 1 Month',
      price: 497,
      period: 'one-time',
      monthlyAfter: 99,
      description: 'Most Popular Choice',
      features: [
        'Complete HyperDrive Manual',
        'Alignment & Entry Criteria',
        'Management Explanations',
        'Back Testing Videos',
        'Prop Firm Guidance',
        '1 Month FREE Live Trading'
      ],
      popular: true,
      cta: 'Get Started'
    },
    {
      name: 'HDOne Manual + 6 Months',
      price: 897,
      period: 'one-time',
      monthlyAfter: 79,
      description: 'Best Value',
      features: [
        'Everything in HDOne Manual',
        '6 Months FREE Live Trading',
        'Priority Support',
        'Extended community access',
        'Lowest monthly rate after'
      ],
      popular: false,
      cta: 'Best Value'
    }
  ];

  const stats = [
    { value: '90%', label: 'Stay in the Top 10%' },
    { value: '500+', label: 'Back-tested Trades' },
    { value: 'Daily', label: 'Live Sessions' }
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="homepage">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-brand">
            <img src={`${process.env.PUBLIC_URL}/logo-original.png`} alt="HDC Logo" className="nav-logo" />
            <div className="brand-text-container">
              <span className="brand-text">HyperDrive</span>
              <span className="brand-tagline">Trading Academy</span>
            </div>
          </div>
          <div className="nav-links">
            <button onClick={() => scrollToSection('home')} className="nav-link">Home</button>
            <button onClick={() => scrollToSection('system')} className="nav-link">The System</button>
            <button onClick={() => scrollToSection('testimonials')} className="nav-link">Testimonials</button>
            <button onClick={() => scrollToSection('pricing')} className="nav-link">Pricing</button>
          </div>
          <div className="nav-actions">
            {isAuthenticated ? (
              <button onClick={() => navigate('/dashboard')} className="nav-login">Dashboard</button>
            ) : (
              <button onClick={() => navigate('/login')} className="nav-login">Login</button>
            )}
            <button onClick={() => scrollToSection('pricing')} className="nav-cta">Join Now</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-video-bg">
          <video 
            autoPlay 
            muted 
            loop 
            playsInline
            preload="auto"
            className="hero-video"
            onCanPlay={(e) => e.target.play().catch(() => {})}
          >
            <source src="/hero-video.mp4" type="video/mp4" />
          </video>
          <div className="hero-video-overlay"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge">The HyperDrive Trading System</div>
          <h1 className="hero-title">
            Recognize. Execute. Manage.
          </h1>
          <p className="hero-subtitle">
            An easy to understand, simple to follow, duplicatable system that allows anyone—brand new or seasoned—to trade for long lasting success.
          </p>
          <div className="hero-actions">
            <button onClick={() => scrollToSection('pricing')} className="hero-cta">
              Start Your Journey
              <ArrowRight size={20} />
            </button>
            <button onClick={() => scrollToSection('system')} className="hero-secondary">
              <Play size={18} />
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* System Section */}
      <section id="system" className="system-section">
        <div className="section-container">
          <h2 className="section-title">The HyperDrive Way</h2>
          <p className="section-subtitle">A proven methodology that takes the guesswork out of trading</p>
          
          <div className="system-content">
            <div className="system-benefits">
              <h3>What Makes HyperDrive Different</h3>
              <ul className="benefits-list">
                {systemBenefits.map((benefit, index) => (
                  <li key={index}>
                    <CheckCircle size={20} className="check-icon" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="system-video">
              <video 
                id="system-video-player"
                muted 
                loop 
                playsInline
                preload="auto"
                poster="/system-poster.jpg" 
                className={`system-video-player ${systemVideoPlaying ? 'playing' : ''}`}
                onPlay={() => setSystemVideoPlaying(true)}
                onPause={() => setSystemVideoPlaying(false)}
              >
                <source src="/system-explainer.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              {!systemVideoPlaying && (
                <button 
                  className="video-play-btn"
                  onClick={() => {
                    const video = document.getElementById('system-video-player');
                    if (video) {
                      video.play().catch(err => console.log('Video play error:', err));
                    }
                  }}
                >
                  <Play size={48} fill="white" />
                </button>
              )}
            </div>
            <div className="system-video-cta-below">
              <p>Ready to master the HyperDrive system?</p>
              <div className="system-cta-buttons">
                <button onClick={() => scrollToSection('pricing')} className="system-cta-primary">
                  Get The Manual
                </button>
                <button onClick={() => scrollToSection('pricing')} className="system-cta-secondary">
                  Join Live Trading
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-container">
          <h2 className="section-title">Everything You Need to Succeed</h2>
          <div className="features-grid">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="feature-card">
                  <div className="feature-icon" style={{ background: `linear-gradient(135deg, ${feature.color}, ${feature.color}dd)` }}>
                    <Icon size={28} />
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="section-container">
          <h2 className="section-title">See What Others Are Saying</h2>
          <div className="testimonials-carousel">
            <button className="carousel-btn prev" onClick={prevTestimonial}>
              <ChevronLeft size={24} />
            </button>
            
            <div className="testimonial-card">
              <Quote size={48} className="quote-icon" />
              <p className="testimonial-quote">"{testimonials[currentTestimonial].quote}"</p>
              <div className="testimonial-author">
                <div className="author-avatar">
                  {testimonials[currentTestimonial].author.charAt(0)}
                </div>
                <div className="author-info">
                  <span className="author-name">~ {testimonials[currentTestimonial].author}</span>
                  <span className="author-role">{testimonials[currentTestimonial].role}</span>
                </div>
              </div>
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} fill="#f59e0b" color="#f59e0b" />
                ))}
              </div>
            </div>
            
            <button className="carousel-btn next" onClick={nextTestimonial}>
              <ChevronRight size={24} />
            </button>
          </div>
          
          <div className="testimonial-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentTestimonial ? 'active' : ''}`}
                onClick={() => setCurrentTestimonial(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="section-container">
          <h2 className="section-title">Start Your Trading Transformation Today</h2>
          
          {loadingMemberships ? (
            <div className="pricing-loading">Loading plans...</div>
          ) : (
            <>
              {/* Featured Course Card (HyperDrive Manual) */}
              {featuredCourses.map((course) => (
                <div key={course.id} className="featured-manual-section">
                  <div className="featured-manual-card">
                    <div className="featured-badge">THE FOUNDATION</div>
                    <div className="featured-content">
                      <div className="featured-left">
                        <h3 className="featured-name">{course.title}</h3>
                        <p className="featured-description">{course.description}</p>
                        <ul className="featured-features">
                          {course.features?.map((feature, idx) => (
                            <li key={idx}>
                              <CheckCircle size={18} />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        <div className="featured-bonus-banner">
                          <span className="bonus-icon">🎁</span>
                          <span className="bonus-text">Includes 30 Days of Live Trading FREE</span>
                        </div>
                      </div>
                      <div className="featured-right">
                        <div className="featured-price">
                          <span className="featured-price-amount">${course.price}</span>
                          <span className="featured-price-period">One-Time</span>
                        </div>
                        <button onClick={() => navigate('/login', { state: { tab: 'register', selectedCourse: course } })} className="featured-cta">
                          {course.cta || 'Get The Manual'}
                        </button>
                        <p className="featured-note">Then $99/mo for continued Live Trading access</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Live Trading Plans */}
              {memberships.filter(m => m.category !== 'indicator').length > 0 && (
              <div className="live-trading-section">
                <h3 className="subsection-title">Live Trading Memberships</h3>
                <p className="subsection-subtitle">Join Coach CJ for daily live trading sessions</p>
                <div className={`pricing-grid ${memberships.filter(m => m.category !== 'indicator').length === 2 ? 'two-col' : 'three-col'}`}>
                  {memberships.filter(m => m.category !== 'indicator').map((membership) => (
                    <div key={membership.id} className={`pricing-card ${membership.popular ? 'popular' : ''}`}>
                      {membership.popular && <div className="popular-badge">MOST POPULAR</div>}
                      <div className="plan-header">
                        <h3 className="plan-name">{membership.name}</h3>
                        <div className="plan-price">
                          <span className="price-amount">${membership.price}</span>
                          <span className="price-period">
                            {formatPeriod(membership.period)}
                          </span>
                        </div>
                      </div>
                      <ul className="plan-features">
                        {membership.features?.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircle size={16} />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <button onClick={() => navigate('/login', { state: { tab: 'register', selectedPlan: membership } })} className="plan-cta">
                        Get Started
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              )}

              {/* HyperDrive Indicator Section - Full Width Card
                  IMPORTANT: This has a special two-column horizontal layout (left: content, right: price box)
                  DO NOT change to a vertical card layout like the other pricing cards.
                  The CSS is in HomePage.css under .indicator-* classes.
                  Editing features/description won't break the layout - it's CSS-protected. */}
              {memberships.filter(m => m.category === 'indicator').map((indicator) => (
                <div key={indicator.id} className="indicator-section" data-testid="indicator-section">
                  <div className="indicator-card" data-testid="indicator-card">
                    <div className="indicator-badge">INDICATOR ONLY</div>
                    <div className="indicator-content">
                      <div className="indicator-left">
                        <h3 className="indicator-name">{indicator.name}</h3>
                        <p className="indicator-description">{indicator.description}</p>
                        <ul className="indicator-features">
                          {indicator.features?.map((feature, idx) => (
                            <li key={idx}>
                              <CheckCircle size={18} />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="indicator-right">
                        <div className="indicator-price">
                          <span className="indicator-price-amount">${indicator.price}</span>
                          <span className="indicator-price-period">{formatPeriod(indicator.period)}</span>
                        </div>
                        <button onClick={() => navigate('/login', { state: { tab: 'register', selectedPlan: indicator } })} className="indicator-cta">
                          {indicator.cta || 'Get Indicator Access'}
                        </button>
                        <p className="indicator-note">No live trading access included</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2 className="cta-title">Ready to Trade the HyperDrive Way?</h2>
          <p className="cta-text">Join traders who are transforming their results with our proven system.</p>
          <button onClick={() => scrollToSection('pricing')} className="cta-button">
            Start Your Journey Today
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="homepage-footer">
        <div className="footer-container">
          <div className="footer-brand">
            <img src={`${process.env.PUBLIC_URL}/logo-original.png`} alt="HDC Logo" className="footer-logo" />
            <div className="brand-text-container">
              <span className="brand-text">HyperDrive</span>
              <span className="brand-tagline">Trading Academy</span>
            </div>
          </div>
          <p className="footer-text">© 2025 HyperDrive Trading Academy. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
